/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { Component, COMPONENT_PLACEMENT_TOP_ONLY } from '../../component-classes';
import { nearestSixteenth } from '../../helpers';
import { Panel } from '../panels/panel.class';
import { PanelItem } from '../panel-items/panel-item.class';
import { panelItemGroupData } from './panel-item-group.data';
import { PanelItemGroups } from './panel-item-groups.class';
import { PanelItems } from '../panel-items/panel-items.class';
import { Publish } from '../../decorators';
import { ReferenceMap } from '../shared.types';

import { Axis, CoordinatePoint3, Vertex3 } from '../../spatial-classes';
import { canMoveCfn, centerItemsVerticallyCfn, getDefaultPlacementCenterXCfn, hasCountertopAppliancesCfn, widthRangeCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { PanelItemGroupDataIn, PanelItemGroupDataOut } from './types';

export class PanelItemGroup extends KitchenDesignBase<PanelItemGroupDataIn> {
	readonly items: PanelItems;
	#center: Vertex3;
	#panel: Panel;
	#positionLocked: boolean;
	#fineTuned: boolean;
	#showCenterDimension: boolean;

	constructor (options:KitchenDesignOptions<PanelItemGroupDataIn> = {}) {
		super(BlueprintInstanceType.PanelItemGroup, options);

		const data = panelItemGroupData(options.dataIn);

		this._id = data.id;
		this.items = new PanelItems({ dataIn: data.items, parent: this });

		// every write to #center.x snaps to the 1/16 grid -- saved designs self-heal on load
		this.#center = new Vertex3({ dataIn: data.center });
		this.#center.x = nearestSixteenth(this.#center.x);
		this.#panel = this.refMap?.panel ?? null;
		this.#positionLocked = data.positionLocked ?? false;
		this.#fineTuned = data.fineTuned ?? false;
		this.#showCenterDimension = data.showCenterDimension ?? true;

		this.setInitialized();
	}

	@Publish('panel_item_group', 'updated')
	set showCenterDimension (value: boolean) { this.#showCenterDimension = value; }
	get showCenterDimension (): boolean { return this.#showCenterDimension; }

	/**
	 * panel item group center represents the position relative to the parent panel
	 * @returns Vertex3 representing the center position relative to the parent panel
	 */
	@Publish('panel_item_group', 'moved')
	set center (value: Vertex3) {
		this.#center.x = nearestSixteenth(value.x);
	}

	get center (): Vertex3 {
		const halfPanelHeight = this.refMap.panel ? this.refMap.panel.size.height / -2 : 0;

		return new Vertex3({ dataIn: {
			x: this.#center.x,
			// always center of panel since panelItemGroup is a virtual container spanning this height of the panel
			y: halfPanelHeight,
			// always virtually centered in panel z
			z: 0,
			readOnly: false
		}});
	}

	/**
	 * panel item group axis represents the rotation/orientation of the panel item group
	 * @returns Axis representing the panel item group's rotation
	 */
	get axis (): Axis {
		return this.#panel?.axis ?? new Axis();
	}

	/**
	 * panel item group origin represents the world space position of the panel item group
	 * origin = parentPanel.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const origin = new Vertex3();
		if (this.#panel) {
			origin.moveTo(this.#panel.origin);
			origin.add(new Vertex3({ dataIn: { x: this.#center.x }}));
		}
		return origin;
	}

	// calculate these:
	get hasCountertopAppliances (): boolean { return hasCountertopAppliancesCfn(this); }
	get hasPassthrough (): boolean { return this.items.children.some((item) => item.item.category?.isPassthrough); }
	get hasVents (): boolean { return this.items.children.some((item) => item.item.category?.isVent); }
	get canMove (): boolean { return canMoveCfn(this) }
	get minWidth (): number { return widthRangeCfn(this)[0] }
	get maxWidth (): number { return widthRangeCfn(this)[1] }

	get maxOutsideWidth (): number { return widthRangeCfn(this, true)[1] }

	/**
	 * Get whether the position of this panel item group is locked
	 * @returns boolean indicating if position is locked
	 */
	get positionLocked (): boolean { return this.#positionLocked; }

	/**
	 * Set whether the position of this panel item group is locked
	 * When locked, the appliance cannot be moved manually or by distribute functions
	 * @param value boolean to lock or unlock position
	 * @emits panel_item_group_position_locked.updated
	 */
	@Publish('panel_item_group_position_locked', 'updated')
	set positionLocked (value: boolean) { this.#positionLocked = value; }

	/**
	 * Placed by hand between its neighbours (`Panel.fineTuneGroupPosition`): the
	 * distributor keeps it where it is, as if position-locked, until a swap, a
	 * reorder or an added appliance resets the panel's fine-tunes.
	 */
	get fineTuned (): boolean { return this.#fineTuned; }

	@Publish('panel_item_group', 'updated')
	set fineTuned (value: boolean) { this.#fineTuned = value; }

	/**
	 * Starting center.x for this group taken from an incoming component's catalog defaultPlacement
	 * @param component Component being added to this group
	 * @returns panel-relative center.x, or null when the component sets no horizontal default
	 */
	public getDefaultPlacementCenterX (component: Component | null): number | null { return getDefaultPlacementCenterXCfn(component, this.#panel?.size.width ?? 0); }

	get height (): number { return this.#panel ? this.#panel.size.height : 0; }
	get width (): number { return this.maxWidth; }
	get depth (): number { return this.#panel ? this.#panel.size.depth : 0; }

	/**
	 * Moves the group to the position provided.
	 *
	 * ABSOLUTE, NOT RELATIVE. `center.x` is ASSIGNED from `position.x` — the
	 * argument is the new position, not an offset to add. The docblock used to
	 * say "deltas", and `positionHorizontallyCfn` was written against that
	 * wording: it passed a shift and the group jumped to the shift amount,
	 * which sent appliances aimed at the far right to the left of centre.
	 *
	 * Out-of-range positions are IGNORED rather than clamped — a request that
	 * would put the group's center past the panel end leaves it where it was.
	 * @param position new center position; only x is honoured
	 * @param isTopOnly measure the bound against the countertop instead of the panel
	 */
	@Publish('panel_item_group', 'moved')
	public move (position: Partial<CoordinatePoint3<number>>, isTopOnly?: boolean): void {
		const halfPanelWidth = isTopOnly
			? this.refMap.island.getCountertop().size.width
			: this.refMap.panel
				? this.refMap.panel.size.width / 2
				: 0;
		if (Math.abs(position.x) <= halfPanelWidth) this.#center.x = nearestSixteenth(position.x);
	}

	@Publish('panel_item_group', 'moved')
	public centerItemsVertically (): void { centerItemsVerticallyCfn(this as PanelItemGroup); }

	/**
	 * max distanceAbove countertop for panel item group
	 * @returns number
	 */
	public getMaxDistanceAboveCountertop (): number {
		return this.items.children.reduce((acc, item) => Math.max(acc, item.item.component.distanceAboveCountertop), 0);
	}

	/**
	 * located the previous panel item group using the center
	 * @returns PanelItemGroup or null when an edge is reaches
	 */
	public getPreviousGroup (withCountertopItems = false): PanelItemGroup | null {
		if (!this.instanceParent || this.instanceParent.instanceOf !== BlueprintInstanceType.PanelItemGroups) return null;
		return (this.instanceParent as unknown as PanelItemGroups).findPreviousByCenter(this.#center as Vertex3, withCountertopItems);
	}

	/**
	 * @param id id to find child reference for
	 * @returns ReferenceMap
	 */
	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['items']);
	}

	/**
	 * locates the next panel item group using the center
	 * @returns PanelItemGroup or null when an edge is reached
	 */
	public getNextGroup (withCountertopItems = false): PanelItemGroup | null {
		if (!this.instanceParent || this.instanceParent.instanceOf !== BlueprintInstanceType.PanelItemGroups) return null;
		return (this.instanceParent as unknown as PanelItemGroups).findNextByCenter(this.#center as Vertex3, withCountertopItems);
	}

	/**
	 * locates the top bound item, if any
	 * @returns PanelItem or null
	 */
	public getTopBoundItem (): PanelItem {
		return (this.items.children.find((pi) => pi.boundTop && pi.item.placement !== COMPONENT_PLACEMENT_TOP_ONLY) as PanelItem) ?? null;
	}

	/**
	 * locates the bottom bound item, if any
	 * @returns PanelItem or null
	 */
	public getBottomBoundItem (): PanelItem {
		return (this.items.children.find((pi) => pi.boundBottom) as PanelItem) ?? null;
	}

	/**
	 * destructor, cleaning up all items before deletion
	 */
	public cleanup (): void {
		this.items.children.forEach((item) => item.cleanup());
		this.items.children.length = 0;
		super.cleanup();
	}

	public serialize (): PanelItemGroupDataOut {
		return {
			id: this._id,
			items: this.items.serialize(),
			center: this.center.serialize(),
			showCenterDimension: this.showCenterDimension,
			positionLocked: this.#positionLocked,
			fineTuned: this.#fineTuned
		};
	}
}
